<template>
	<v-text-field
		outlined
		single-line
		hide-details
		:loading="loading"
		class="search-field"
		@keydown="onKeyDown"
		ref="data_input_search"
		@blur="$emit('on-blur')"
		:placeholder="placeholder"
		v-model="localSearchString"
		@click:prepend-inner="unExpandItem"
		:class="{ 'bottom-to-top-layout': isBottomToTopLayout }"
		:append-icon="hasClearIcon ? 'mdi-close-circle-outline' : null"
		@click:append="hasClearIcon ? clearSearchString() : refreshData()"
		:prepend-inner-icon="hasBackIcon ? 'mdi-keyboard-backspace' : null"
	>
		<template v-slot:append>
			<AnemicButton @click="toggleEmojiPickerMode" class="emoji-button flex-centerize" v-if="showEmojiPickerButton">
				<v-icon class="emoji-icon">mdi-emoticon-happy-outline</v-icon>
			</AnemicButton>
			<AnemicButton @click="toggleSourceContent" class="content-switcher-button" v-if="allowPreviousStepsSelection && !disableMappedData">
				{{ contentSwitcherTitle }}
			</AnemicButton>
			<AnemicButton @click="refreshData" class="refresh-button flex-centerize" v-if="!isPreviousStepsSelectionMode && hasRefreshButton">
				{{ 'Refresh' }}
				<v-icon class="refresh-icon">mdi-refresh</v-icon>
			</AnemicButton>
		</template>
	</v-text-field>
</template>
<script>
import debounce from 'lodash/debounce';
import { REFRESH_DATA, DATA_INPUT_MENU_SEARCH_FIELD_HEIGHT } from './data-input-menu.const';
import { Key } from 'ts-key-enum';
export default {
	name: 'DataInputSearch',
	props: {
		loading: { type: Boolean },
		hasBackIcon: { type: Boolean },
		searchString: { type: String },
		hasClearIcon: { type: Boolean },
		hasRefreshButton: { type: Boolean },
		isTableColumnInput: { type: Boolean },
		isBottomToTopLayout: { type: Boolean },
		isShowingEmojiPicker: { type: Boolean },
		showEmojiPickerButton: { type: Boolean },
		disableMappedData: { type: Boolean },
		allowPreviousStepsSelection: { type: Boolean },
		isPreviousStepsSelectionMode: { type: Boolean },
	},
	computed: {
		localSearchString: {
			get() {
				return this.searchString;
			},
			set: debounce(function (value) {
				this.$emit('update:searchString', value);
			}, 100),
		},
		placeholder() {
			return this.isShowingEmojiPicker
				? 'Search emoji'
				: this.isPreviousStepsSelectionMode
				? 'Search data'
				: `Search ${this.isTableColumnInput ? 'in selected table' : 'options'}`;
		},
		contentSwitcherTitle() {
			return this.isPreviousStepsSelectionMode ? `Select from ${this.isTableColumnInput ? 'table' : 'options'}` : 'Map data';
		},
	},
	methods: {
		toggleSourceContent() {
			this.$emit('toggle-source-content');
		},
		toggleEmojiPickerMode() {
			this.$emit('toggle-emoji-picker-mode');
		},
		clearSearchString() {
			this.$emit('update:searchString', '');
		},
		unExpandItem() {
			this.clearSearchString();
			this.$emit('un-expand-item');
			this.$emit('close-emoji-picker-mode');
		},
		refreshData() {
			this.$emit(REFRESH_DATA);
		},
		focus() {
			// Triggerd from parent
			this.$refs.data_input_search.focus();
		},
		onKeyDown(e) {
			if (this.$refs.data_input_search?.isFocused) {
				if (e.key === Key.Enter) {
					this.$emit('on-enter');
				}
				if (e.key === Key.ArrowUp) {
					this.$emit('on-arrow-up');
				}
				if (e.key === Key.ArrowDown) {
					this.$emit('on-arrow-down');
				}
				if (e.key === Key.ArrowLeft && !this.searchString) {
					this.$emit('on-arrow-left');
				}
				if (e.key === Key.ArrowRight && !this.searchString) {
					this.$emit('on-arrow-right');
				}
			}
		},
		data() {
			return {
				DATA_INPUT_MENU_SEARCH_FIELD_HEIGHT: String(DATA_INPUT_MENU_SEARCH_FIELD_HEIGHT) + 'px',
			};
		},
	},
};
</script>

<style scoped lang="scss">
.search-field {
	$height: var(--DATA_INPUT_MENU_SEARCH_FIELD_HEIGHT);

	box-shadow: 0px 1px 2px #747f8d55 !important;

	.content-switcher-button {
		height: 20px;
		width: 126px;
		margin-top: 1px;
		border: 1px solid;
		border-radius: 5px;
		color: var(--app-purple);
		font-size: 12px !important;
		background: var(--bg-color);
	}

	.refresh-button {
		width: 62px;
		height: 20px;
		margin-top: 1px;
		margin-left: 6px;
		border-radius: 5px;
		font-size: 12px !important;
		color: var(--insert-data-black);
		background: var(--insert-data-grey);

		.refresh-icon {
			width: 9px;
			height: 9px;
			margin-left: 3px;
			color: var(--insert-data-black);
		}
	}
	.emoji-button {
		width: 14px;
		height: 20px;
		margin-top: 1px;
		margin-right: -8px;
		border-radius: 5px;
		background: transparent;
		font-size: 12px !important;
		color: var(--insert-data-black);

		.emoji-icon {
			width: 9px;
			height: 9px;
			color: grey;
			margin-left: 3px;
		}
	}

	&::v-deep {
		&.v-text-field {
			font-size: 12px !important;
			font-weight: normal !important;
			background: var(--bg-color) !important;
			&.v-text-field--outlined {
				.v-input__control > .v-input__slot fieldset {
					border-radius: 8px 8px 0px 0px !important;
				}
				&.bottom-to-top-layout > .v-input__control > .v-input__slot fieldset {
					border-radius: 0px 0px 8px 8px !important;
					box-shadow: 0px -1px 2px #747f8d55 !important;
				}
			}
		}
		&.v-text-field .v-input__control .v-input__slot {
			min-height: $height !important;
			max-height: $height !important;
		}

		&.v-text-field--outlined fieldset {
			border-color: var(--forth-border-color);
		}

		&.v-text-field--enclosed .v-input__prepend-inner {
			width: 25px;
			height: 25px;
			margin-top: 3px;
			margin-left: -9px;
			margin-right: 4px;
			border-radius: 4px;
			border: 1px solid var(--grey-background);
			background: var(--insert-data-key-background);
		}
		&.v-text-field .v-input__append-inner {
			margin-top: 4px !important;
		}
		.v-icon {
			font-size: 16px !important;
		}
	}
}
</style>
